<script>
  import { onMount, onDestroy } from 'svelte';
  import { navigate as goto } from 'svelte-routing';
  import ParticipantDetails from '$lib/ParticipantDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import Modal from './messages/MessageModal.svelte';
  import { selectedStudy, selectedSite, selectedParticipant } from '$stores/selectionStore';
  import { studyProperties } from '$stores/configStore';
  import { refreshCurrentSiteCount, forceRefreshSiteCount } from '$stores/messageCountStore';
  import { refreshCurrentParticipantCount, forceRefreshParticipantCount } from '$stores/participantMessageCountStore';
  import { userRole } from '$stores/sessionStore';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Footer from '$lib/Footer.svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import {
    faRotate,
    faPlus,
    faChevronRight,
    faLock,
    faEllipsis,
    faCheckDouble,
    faBell,
    faBellExclamation
  } from '@fortawesome/pro-light-svg-icons';
  
  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  import CreateMessageModal from './messages/CreateMessageModal.svelte';

  let messages = [];
  let resultCount = 0;
  let currentPage = 1;
  let totalPages = 1;
  let limit = 20;
  let isLoading = true;
  let searchText = '';
  let selectedType = 'ALL';
  let selectedStatus = 'ALL';

  let isModalOpen = false;
  let messageTitle = '';
  let messageBody = '';

  let messageMenuExpanded = false;
  let showCreateMessageModal = false;
  let newMessageTitle = 'Reminder';
  let newMessageText = '';
  let isMarkingAsRead = false;

  // Feature flag for PIN Reset
  let isPinResetEnabled = false;
  let isTogglingMute = false;
  let showUnmutePopover = false;
  let unmutePopoverContainer;

  const UNMUTE_DAY_OPTIONS = [1, 7, 10, 14, 15, 30];

  $: isTemporarilyUnmuted = $selectedParticipant?.muted === false
    && $selectedParticipant?.muteResumeDate
    && new Date($selectedParticipant.muteResumeDate) > new Date();

  // Reactive statement to update feature flag based on study properties
  $: {
    if ($studyProperties?.features?.pinReset !== undefined) {
      isPinResetEnabled = $studyProperties.features.pinReset === true;
    } else {
      isPinResetEnabled = false;
    }
  }

  function handleMuteButtonClick() {
    if (!$selectedParticipant || isTogglingMute) return;
    if ($selectedParticipant.muted) {
      showUnmutePopover = !showUnmutePopover;
    } else {
      sendMuteUpdate({ muted: true });
    }
  }

  async function handleUnmuteSelection(days) {
    showUnmutePopover = false;
    const body = days ? { muted: false, unmuteDays: days } : { muted: false };
    await sendMuteUpdate(body);
  }

  async function sendMuteUpdate(body) {
    if (isTogglingMute) return;
    const action = body.muted ? 'mute' : 'unmute';
    try {
      isTogglingMute = true;
      const url = `${API_BASE_URL}/persons/${$selectedParticipant._id}/muted`;
      const response = await fetch(url, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error(`Mute toggle failed: ${response.status} ${response.statusText}`, errorBody);
        throw new Error(`Failed to ${action} participant (${response.status})`);
      }

      const result = await response.json();
      const doc = result.data || result;
      $selectedParticipant = { ...$selectedParticipant, muted: doc.muted, muteResumeDate: doc.muteResumeDate ?? null };
    } catch (error) {
      console.error('Error toggling muted state:', error);
      alert(`Failed to ${action} participant. Please try again.`);
    } finally {
      isTogglingMute = false;
    }
  }

  const RTL_LANGUAGES = [
    'Arabic',
    'Hebrew',
    'Persian',
    'Farsi',
    'Urdu',
    'Aramaic',
    "N'Ko",
    'Mandaic',
    'Samaritan',
    'Syriac',
  ];

  function isRTL(language) {
    return RTL_LANGUAGES.includes(language);
  }

  onMount(async () => {
    if ($selectedParticipant) {
      fetchMessages(1);
      // Refresh both site and participant message counts when viewing participant messages
      try {
        await refreshCurrentSiteCount();
      } catch (error) {
        console.log('Could not refresh site message count:', error.message);
      }
      try {
        await refreshCurrentParticipantCount();
      } catch (error) {
        console.log('Could not refresh participant message count:', error.message);
      }
    } else {
      isLoading = false;
    }
  });

  async function fetchMessages(page = 1) {
    try {
      isLoading = true;
      let url = `${API_BASE_URL}/messages?page=${page}&pageSize=${limit}&subjectGUID=${$selectedParticipant._id}`;

      if (selectedType !== 'ALL') {
        url += `&type=${selectedType}`;
      }

      if (selectedStatus !== 'ALL') {
        url += `&status=${selectedStatus}`;
      }

      const res = await fetch(url);

      if (!res.ok) {
        throw new Error('Failed to fetch messages');
      }

      const responseData = await res.json();

      resultCount = responseData.totalCount || 0;
      currentPage = responseData.page || 1;
      totalPages = responseData.totalPages || 1;

      messages = responseData.data;
    } catch (err) {
      console.error('Error fetching log entries:', err);
      messages = [];
    } finally {
      isLoading = false;
    }
  }

  async function handleRefresh() {
    //console.log("handleRefresh");
    fetchMessages(1);
    // Also refresh both site and participant message counts when manually refreshing
    try {
      await refreshCurrentSiteCount();
    } catch (error) {
      console.log('Could not refresh site message count:', error.message);
    }
    try {
      await refreshCurrentParticipantCount();
    } catch (error) {
      console.log('Could not refresh participant message count:', error.message);
    }
  }

  function handlePageChange(newPage) {
    if (newPage > 0 && newPage <= totalPages) {
      fetchMessages(newPage);
    }
  }

  function closeModal() {
    isModalOpen = false;
    messageTitle = '';
    messageBody = '';
  }

  function showModal(title, body) {
    isModalOpen = true;
    messageTitle = title;
    messageBody = body;
  }

  function handleMessageMenuExpand() {
    messageMenuExpanded = !messageMenuExpanded;
  }

  async function createPINMessage() {
    const confirmed = confirm(
      'Are you sure you want to send a PIN reset message for this participant?'
    );

    if (!confirmed) return;

    await createMessage('Reminder', 'Your PIN has been reset.');
  }

  async function createMessage(title, message) {
    try {
      // Get site GUID from participant if selectedSite is not available
      const siteGUID = $selectedSite?._id || $selectedParticipant?.siteGUID;
      
      if (!siteGUID) {
        throw new Error('No site information available for creating message');
      }
      
      const messageData = {
        subjectGUID: $selectedParticipant._id,
        siteGUID: siteGUID,
        title,
        message,
        messageStatusGroupCode: 'MSGSTATUS',
        messageStatusCode: 'MSGSTATUSSENT',
        receiverTypeGroupCode: 'RECEIVERTYPE',
        receiverTypeCode: 'MSGRCVRSUBBJECT',
        messageTypeGroupCode: 'MSGTYPE',
        messageTypeCode: 'MSGREMINDER',
      };

      const response = await fetch(`${API_BASE_URL}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(messageData),
      });

      if (!response.ok) {
        throw new Error('Failed to create message');
      }

      // Refresh the messages list and count
      await fetchMessages(1);
      try {
        await refreshCurrentSiteCount();
      } catch (error) {
        console.log('Could not refresh site message count:', error.message);
      }
      try {
        await refreshCurrentParticipantCount();
      } catch (error) {
        console.log('Could not refresh participant message count:', error.message);
      }
    } catch (error) {
      console.error('Error creating message:', error);
    }
  }

  function handleCreateCustomMessage() {
    showCreateMessageModal = true;
    messageMenuExpanded = false;
  }

  async function handleSubmitCustomMessage(event) {
    const { title, message } = event.detail;
    if (!title || !message) {
      alert('Please fill in both title and message');
      return;
    }

    await createMessage(title, message);
    showCreateMessageModal = false;
    newMessageTitle = '';
    newMessageText = '';
  }

  // Click outside handler
  let menuContainer;

  function handleClickOutside(event) {
    if (menuContainer && !menuContainer.contains(event.target)) {
      messageMenuExpanded = false;
    }
    if (unmutePopoverContainer && !unmutePopoverContainer.contains(event.target)) {
      showUnmutePopover = false;
    }
  }

  onMount(() => {
    if (typeof window !== 'undefined') {
      document.addEventListener('click', handleClickOutside);
    }
  });

  onDestroy(() => {
    if (typeof window !== 'undefined') {
      document.removeEventListener('click', handleClickOutside);
    }
  });

  async function markAllAsRead() {
    if (!$selectedParticipant) return;
    
    const confirmed = confirm('Are you sure you want to mark all "Sent" messages as "Read"?');
    if (!confirmed) return;
    
    try {
      isMarkingAsRead = true;
      
      const response = await fetch(`${API_BASE_URL}/messages/markSubjectAsRead`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          subjectGUID: $selectedParticipant._id
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to mark messages as read');
      }
      
      const result = await response.json();
      
      // Show success message
      // alert(`Successfully marked ${result.modifiedCount} messages as read.`);
      
      // Refresh the messages list and count - handle these separately to avoid masking the success
      try {
        await fetchMessages(1);
      } catch (refreshError) {
        console.error('Error refreshing messages list:', refreshError);
        // Don't show error to user since the main operation succeeded
      }

      try {
        await refreshCurrentSiteCount();
        // Force refresh the sidebar count to ensure it updates immediately
        if ($selectedSite) {
          await forceRefreshSiteCount($selectedSite._id);
        }
        // Force refresh the participant message count
        await forceRefreshParticipantCount($selectedParticipant._id);
      } catch (countError) {
        console.error('Error refreshing message count:', countError);
        // Don't show error to user since the main operation succeeded
      }
      
    } catch (error) {
      console.error('Error marking messages as read:', error);
      alert('Failed to mark messages as read. Please try again.');
    } finally {
      isMarkingAsRead = false;
    }
  }
</script>

<StudyHeader />

<FeatureInfo 
  title="Participant Messages" 
  description="This page displays messages sent to the selected participant. You can filter by message type and status (coming soon), 
  view message details, and send new messages including PIN reset notifications. Click on a message to view its full content and a translation to English."
  marginBottom="mb-0"
/>

{#if $selectedParticipant}
  <ParticipantDetails showOptionsMenu={false} />
{/if}

<Toolbar title="Messages" itemCountTitle="Messages" itemCount={resultCount}>
  <div class="flex items-center gap-2">
    <div class="text-m mb-0">
      Type
      <select
        id="type"
        class="form-select h-6 w-40 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
        bind:value={selectedType}
      >
        <option value="ALL">All</option>
        <option value="Alert">Alert</option>
        <option value="Reminder">Reminder</option>
        <option value="CGM Recheck">CGM Recheck</option>
      </select>
    </div>
    <div class="text-m mb-0">
      Status
      <select
        id="status"
        class="form-select h-6 w-28 border px-2 p-0 rounded text-sm dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 bg-white appearance-none"
        bind:value={selectedStatus}
      >
        <option value="ALL">All</option>
        <option value="Sent">Sent</option>
        <option value="Read">Read</option>
      </select>
    </div>
    <div class="flex gap-2">
      {#if $studyProperties?.features?.messageMuting}
        <div class="relative" bind:this={unmutePopoverContainer}>
          <button
            on:click={handleMuteButtonClick}
            class="pagination mx-1 p-0 ml-4"
            title={isTemporarilyUnmuted
              ? `Temporarily unmuted until ${new Date($selectedParticipant.muteResumeDate).toLocaleDateString()} — click to re-mute`
              : $selectedParticipant?.muted
                ? 'Unmute alerts for this participant'
                : 'Mute alerts for this participant'}
            disabled={isTogglingMute}
          >
            {#if isTemporarilyUnmuted}
              <FontAwesomeIcon icon={faBell} class="{isTogglingMute ? 'animate-pulse' : ''} text-amber-500" />
            {:else if $selectedParticipant?.muted}
              <FontAwesomeIcon icon={faBellExclamation} class="{isTogglingMute ? 'animate-pulse' : ''} text-red-500" />
            {:else}
              <FontAwesomeIcon icon={faBell} class={isTogglingMute ? 'animate-pulse' : ''} />
            {/if}
          </button>
          {#if showUnmutePopover}
            <div class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-10">
              <div class="px-4 py-1 text-xs text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">Unmute for...</div>
              {#each UNMUTE_DAY_OPTIONS as days}
                <button
                  on:click={() => handleUnmuteSelection(days)}
                  class="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  {days} {days === 1 ? 'day' : 'days'}
                </button>
              {/each}
              <div class="border-t dark:border-gray-700"></div>
              <button
                on:click={() => handleUnmuteSelection(null)}
                class="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Permanently
              </button>
            </div>
          {/if}
        </div>
      {/if}
      <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
        <FontAwesomeIcon icon={faRotate} />
      </button>
      <button 
        on:click={markAllAsRead} 
        class="pagination mx-1 p-0" 
        title="Mark all Sent messages as Read"
        disabled={isMarkingAsRead}
      >
        <FontAwesomeIcon icon={faCheckDouble} class={isMarkingAsRead ? 'animate-spin' : ''} />
      </button>
      <div class="relative" bind:this={menuContainer}>
        <button on:click={handleMessageMenuExpand} class="pagination mx-1 p-0" title="More options">
          <FontAwesomeIcon icon={faEllipsis} />
        </button>
        {#if messageMenuExpanded}
          <div
            class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-10"
          >
            <button
              on:click={handleCreateCustomMessage}
              class="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <FontAwesomeIcon icon={faPlus} class="mr-2" />
              Create Message
            </button>
            {#if isPinResetEnabled}
              <button
                on:click={createPINMessage}
                class="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <FontAwesomeIcon icon={faLock} class="mr-2" />
                Reset PIN
              </button>
            {/if}
          </div>
        {/if}
      </div>
    </div>
  </div>
</Toolbar>

{#if isLoading}
  <div class="flex flex-col justify-center items-center h-64 bg-stone-100/50 dark:bg-gray-725 mx-1">
    <div class="loader mb-4"></div>
    <p class="text-xs justify-center text-gray-700 dark:text-gray-300">
      preparing messages data
    </p>
  </div>
{:else}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Title</th>
          <th class="px-4 py-2 text-left bold">Message</th>
          <th class="px-4 py-2 text-left bold">Type</th>
          <th class="px-4 py-2 text-left bold">Status</th>
          <th class="px-4 py-2 text-left bold">Created (Local)</th>
          <th class="px-4 py-2 text-left bold">Read (Local)</th>
        </tr>
      </thead>
      <tbody>
        {#each messages as message, index}
          <tr
            class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index %
              2 ===
            0
              ? 'bg-stone-50 dark:bg-gray-725'
              : 'bg-stone-75 dark:bg-gray-750'}"
          >
            <td class="px-4 py-2">{message.title}</td>
            <td class="px-4 py-2 w-1/3 cursor-pointer" on:click={() => showModal(message.title, message.message)}>{message.message}</td>
            <td class="px-4 py-2">{message.type}</td>
            <td class="px-4 py-2">{message.status}</td>
            <td class="px-4 py-2"
              >{formatToLocalDateTime(message.created, $selectedParticipant.timezoneCode)}</td
            >
            <td class="px-4 py-2"
              >{message.read
                ? formatToLocalDateTime(message.read, $selectedParticipant.timezoneCode)
                : ''}</td
            >
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<!-- Pagination -->
<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
</Footer>

<!-- Message Modal -->
{#if isModalOpen}
  <Modal 
    isOpen={isModalOpen}
    messageTitle={messageTitle} 
    messageBody={messageBody} 
    participantLanguage={$selectedParticipant?.language || 'English'}
    on:close={closeModal} 
  />
{/if}

<!-- Create Message Modal -->
{#if showCreateMessageModal}
  <CreateMessageModal 
    isOpen={showCreateMessageModal}
    on:submit={handleSubmitCustomMessage} 
    on:close={() => (showCreateMessageModal = false)} 
  />
{/if}

<style>
  .loader {
    border: 2px solid #20669c;
    border-radius: 50%;
    border-top: 4px solid #98acb4;
    width: 32px;
    height: 32px;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
